import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Copy, Trash2, ShieldCheck } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * Iter 182 — Owner inline controls on BusinessDetail.jsx.
 *
 * Renders ✏️ Edit / 📋 Duplicate / 🗑️ Delete for the verified owner of a
 * business and a single 🛠️ "Open in Admin" shortcut for admins. The Edit and
 * Duplicate buttons deep-link to the existing Business Dashboard editor via
 * the `?edit=` / `?duplicate=` query string handled by `useBusinessDashboard`.
 * Delete calls the existing soft-delete endpoint (`DELETE
 * /api/business/(offer|event)/{id}`) directly so the owner doesn't have to
 * leave the page.
 *
 * Props:
 *   - kind: 'offer' | 'event'
 *   - itemId: string
 *   - itemTitle: string (for the confirm prompt)
 *   - user: current logged-in user
 *   - business: business doc shown on the page
 *   - businessId: id of the business the item belongs to (admin route)
 *   - isPreview: hide controls when previewing the public view
 *   - onDeleted: callback so the parent can drop the row from local state
 */
const OwnerInlineControls = ({
  kind,
  itemId,
  itemTitle,
  user,
  business,
  businessId,
  isPreview,
  onDeleted,
}) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (isPreview) return null;
  if (!user) return null;

  const isOwner =
    user.role === 'business' &&
    business &&
    business.userId &&
    business.userId === (user.userId || user.id);
  const isAdmin = user.role === 'admin';

  if (!isOwner && !isAdmin) return null;

  const baseBtn =
    'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors';

  if (isAdmin && !isOwner) {
    return (
      <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-controls-${kind}-${itemId}`}>
        <button
          type="button"
          onClick={() => navigate(`/admin/business/${businessId}`)}
          className={`${baseBtn} bg-slate-100 text-slate-700 hover:bg-slate-200`}
          data-testid={`admin-open-${kind}-${itemId}`}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          Open in Admin
        </button>
      </div>
    );
  }

  const handleEdit = () => {
    navigate(`/business/dashboard?edit=${kind}:${itemId}`);
  };
  const handleDuplicate = () => {
    navigate(`/business/dashboard?duplicate=${kind}:${itemId}`);
  };
  const handleDelete = async () => {
    if (busy) return;
    const label = kind === 'offer' ? 'offer' : 'event';
    if (!window.confirm(`Archive this ${label}?\n\n"${itemTitle}"\n\nIt will be hidden from the public listing but kept in your dashboard's archive.`)) return;
    setBusy(true);
    try {
      await axiosInstance.delete(`/business/${kind}/${itemId}`);
      onDeleted?.(itemId);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Could not archive — please try again.';
      window.alert(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-2 flex flex-wrap gap-2"
      data-testid={`owner-controls-${kind}-${itemId}`}
    >
      <button
        type="button"
        onClick={handleEdit}
        className={`${baseBtn} bg-purple-50 text-purple-700 hover:bg-purple-100`}
        data-testid={`owner-edit-${kind}-${itemId}`}
      >
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </button>
      <button
        type="button"
        onClick={handleDuplicate}
        className={`${baseBtn} bg-blue-50 text-blue-700 hover:bg-blue-100`}
        data-testid={`owner-duplicate-${kind}-${itemId}`}
      >
        <Copy className="h-3.5 w-3.5" />
        Duplicate
      </button>
      <button
        type="button"
        onClick={handleDelete}
        disabled={busy}
        className={`${baseBtn} bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60`}
        data-testid={`owner-delete-${kind}-${itemId}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
        {busy ? 'Archiving…' : 'Delete'}
      </button>
    </div>
  );
};

export default OwnerInlineControls;
